import React from "react";
import { X, MapPin, Clock, Navigation, Truck, Hash, ExternalLink } from "lucide-react";

const DIR_LABEL = { N: "North", S: "South", E: "East", W: "West" };

const formatDateTime = (raw) => {
  if (!raw) return "—";
  const dt = new Date(raw.replace(" ", "T"));
  if (isNaN(dt)) return raw;
  return dt.toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit", hour12: false,
  });
};

const DetailRow = ({ icon: Icon, label, value, href }) => (
  <div className="flex flex-col gap-0.5 py-2.5 border-b border-slate-100 last:border-0">
    <div className="flex items-center gap-1.5 text-[10px] font-medium text-slate-400 uppercase tracking-wider">
      <Icon size={10} aria-hidden="true" />
      {label}
    </div>
    {href ? (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-1.5 text-xs font-medium text-amber-600 hover:text-amber-800 transition-colors"
      >
        <ExternalLink size={11} />
        View on map
      </a>
    ) : (
      <p className="text-xs font-semibold text-slate-700">{value || "—"}</p>
    )}
  </div>
);

const TransactionDetailsPanel = ({ transaction, onClose }) => {
  if (!transaction) return null;

  let mapsHref = null;
  if (transaction.tollPlazaGeocode) {
    const [lat, lng] = transaction.tollPlazaGeocode.split(",").map((s) => s.trim());
    mapsHref = `https://maps.google.com/?q=${lat},${lng}`;
  }

  return (
    <div className="w-56 flex-shrink-0 bg-white border-l border-slate-200 flex flex-col overflow-hidden">

      {/* Header */}
      <div className="bg-slate-900 text-white px-4 py-3 flex items-start justify-between flex-shrink-0">
        <div>
          <p className="text-xs font-semibold">Transaction Detail</p>
          <p className="text-[10px] text-slate-400 mt-0.5 leading-snug line-clamp-2">
            {transaction.tollPlazaName}
          </p>
        </div>
        <button
          onClick={onClose}
          className="w-6 h-6 rounded-full bg-slate-700 hover:bg-slate-600 flex items-center justify-center text-slate-300 hover:text-white transition-colors flex-shrink-0 ml-2"
          aria-label="Close"
        >
          <X size={12} />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-4 py-1">
        <DetailRow icon={MapPin}     label="Toll Plaza"   value={transaction.tollPlazaName} />
        <DetailRow icon={Clock}      label="Date & Time"  value={formatDateTime(transaction.readerReadTime)} />
        <DetailRow icon={Navigation} label="Direction"    value={DIR_LABEL[transaction.laneDirection] || transaction.laneDirection} />
        <DetailRow icon={Truck}      label="Vehicle Type" value={transaction.vehicleType} />
        <DetailRow
          icon={Hash}
          label="Sequence ID"
          value={transaction.seqNo ? `${transaction.seqNo.toString().substring(0, 18)}…` : "—"}
        />
        <DetailRow icon={MapPin} label="Location" href={mapsHref} />
      </div>

      {/* Focus button */}
      <div className="px-4 py-3 border-t border-slate-200 flex-shrink-0">
        <button
          onClick={onClose}
          className="w-full flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-500 text-slate-900 text-xs font-bold py-2 rounded-lg transition-colors"
        >
          <MapPin size={12} />
          Close Detail
        </button>
      </div>
    </div>
  );
};

export default TransactionDetailsPanel;